import { ChartLineStyle } from '../../../colorSelector/lineStyle.types'

export const CP_DEFAULT_PRICE_LINE_COLOR = '#9C27B0'
export const CP_DEFAULT_NECK_LINE_COLOR = '#9C27B0'
export const CP_DEFAULT_BACKGROUND_COLOR = '#9C27B033'
export const CP_DEFAULT_PANE_LABEL_COLOR = '#9C27B0'
export const CP_DEFAULT_PANE_LABEL_TEXT_COLOR = '#FFFFFF'
export const CP_DEFAULT_PRICE_LINE_WIDTH = 2
export const CP_DEFAULT_NECK_LINE_WIDTH = 2

export const CHART_PATTERN_LINE_STYLE_OPTIONS: { label: string; value: ChartLineStyle }[] = [
    { label: 'Solid', value: 'solid' },
    { label: 'Dotted', value: 'dotted' },
    { label: 'Dashed', value: 'dashed' },
    { label: 'Large Dashed', value: 'largeDashed' },
    { label: 'Sparse Dotted', value: 'sparseDotted' },
]

/** TradingView-style candlestick pattern marker colors */
export const CP_CANDLESTICK_MARKER_COLOR = '#2962FF'
export const CP_CANDLESTICK_BEARISH_MARKER_COLOR = '#F23645'
export const CP_CANDLESTICK_CANDLE_FILL_OPACITY = 0.08

export const chartLineStyleToLineType = (style: string | undefined) => {
    switch (style) {
        case 'dotted': return 'dotted' as const
        case 'dashed':
        case 'largeDashed':
        case 'sparseDotted': return 'dashed' as const
        default: return 'normal' as const
    }
}

const toBool = (value: unknown, fallback: boolean): boolean => {
    if (value === true || value === 'true') return true
    if (value === false || value === 'false') return false
    return fallback
}

/** Coerce hex string or `{ color, opacity }` runtime value to a hex color string. */
export const toPatternColorString = (value: unknown, fallback: string): string => {
    if (typeof value === 'string' && value) return value
    if (value && typeof value === 'object' && 'color' in (value as Record<string, unknown>)) {
        const color = (value as { color?: string }).color
        if (typeof color === 'string' && color) return color
    }
    return fallback
}

/** Flat map from proto `{ settings }`, DB flat map, or runtime script `draw({ settings })`. */
export const getPatternDrawingSettingsMap = (
    drawingSettings?: unknown,
    scriptSettings?: Record<string, unknown>,
): Record<string, unknown> => {
    if (!drawingSettings || typeof drawingSettings !== 'object') {
        return { ...(scriptSettings ?? {}) }
    }
    const raw = drawingSettings as Record<string, unknown> & { settings?: Record<string, unknown> }
    const nestedSettings = raw.settings && typeof raw.settings === 'object' ? raw.settings : undefined
    const { settings: _nested, patterns: _patterns, ...topLevel } = raw
    return {
        ...topLevel,
        ...(nestedSettings ?? {}),
        ...(scriptSettings ?? {}),
    }
}

/** Merge updates into drawing settings using the proto `{ settings }` shape. */
export const buildPatternDrawingSettingsUpdate = (
    drawingSettings: unknown,
    patch: Record<string, unknown>,
): { settings: Record<string, unknown> } => ({
    settings: {
        ...getPatternDrawingSettingsMap(drawingSettings),
        ...patch,
    },
})

export const resolveChartPatternOverlaySettings = (raw: Record<string, unknown> | undefined) => {
    const nested = getPatternDrawingSettingsMap(raw)
    return {
        priceLineColor: toPatternColorString(
            nested.priceLineColor ?? nested.lineColor,
            CP_DEFAULT_PRICE_LINE_COLOR,
        ),
        priceLineStyle: (nested.priceLineStyle ?? 'solid') as string,
        priceLineWidth: Number(nested.priceLineWidth ?? nested.lineWidth ?? CP_DEFAULT_PRICE_LINE_WIDTH),
        neckLineColor: toPatternColorString(
            nested.neckLineColor ?? nested.lineColor,
            CP_DEFAULT_NECK_LINE_COLOR,
        ),
        neckLineStyle: (nested.neckLineStyle ?? 'dotted') as string,
        neckLineWidth: Number(nested.neckLineWidth ?? nested.lineWidth ?? CP_DEFAULT_NECK_LINE_WIDTH),
        showBackground: toBool(nested.showBackground ?? nested.showLineFills, true),
        backgroundColor: toPatternColorString(
            nested.backgroundColor ?? nested.fillColor,
            CP_DEFAULT_BACKGROUND_COLOR,
        ),
        showPaneLabels: toBool(nested.showPaneLabels ?? nested.showLabels, true),
        paneLabelColor: toPatternColorString(nested.paneLabelColor, CP_DEFAULT_PANE_LABEL_COLOR),
        paneLabelTextColor: toPatternColorString(
            nested.paneLabelTextColor,
            CP_DEFAULT_PANE_LABEL_TEXT_COLOR,
        ),
        showLines: toBool(nested.showLines ?? nested.showTrendlines, true),
        showPolylines: toBool(nested.showPolylines ?? nested.showPatternLines, true),
        showTrendlines: toBool(nested.showTrendlines ?? nested.showLines, true),
        showPatternLines: toBool(nested.showPatternLines ?? nested.showPolylines, true),
        showPriceTarget: toBool(nested.showPriceTarget, true),
        labelColorBullish: toPatternColorString(
            nested.labelColorBullish ?? nested.paneLabelColor,
            CP_CANDLESTICK_MARKER_COLOR,
        ),
        labelColorBearish: toPatternColorString(
            nested.labelColorBearish,
            CP_CANDLESTICK_BEARISH_MARKER_COLOR,
        ),
        candleFillOpacity: Number(nested.candleFillOpacity ?? CP_CANDLESTICK_CANDLE_FILL_OPACITY),
    }
}
